import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext({ theme: 'dark', toggle: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(
    () => localStorage.getItem('cuzo_theme') || 'dark'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('cuzo_theme', theme);
  }, [theme]);

  // Apply on first mount too
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, []); // eslint-disable-line

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, toggle, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// Convenience: returns theme-aware color values
export function useColors() {
  const { isDark } = useTheme();
  return isDark ? DARK : LIGHT;
}

export const DARK = {
  bg:       '#090909',
  s1:       '#101012',
  s2:       '#171719',
  s3:       '#1F1F22',
  s4:       '#29292D',
  text1:    '#F2F2EE',
  text2:    '#D1D2CE',
  text3:    '#A6A8A4',
  text4:    '#7D807C',
  border:   'rgba(244,244,241,0.10)',
  borderMd: 'rgba(244,244,241,0.17)',
  borderHi: 'rgba(244,244,241,0.28)',
  accent:   '#FF63AA',
  accentOn: '#090909',
  brand:    '#FF63AA',
  brandOn:  '#090909',
  // Ordered accent ramp, largest share first. Parts of one measure (revenue
  // mix, whale tiers) are sequential data, so they step one hue by lightness
  // rather than borrowing a second and third hue. Stepped per theme because a
  // step that clears the dark surface disappears on the light one.
  // Validated on this surface: adjacent separation and contrast both pass.
  seq1:     '#FFC2DD',
  seq2:     '#FF63AA',
  seq3:     '#B83C74',
  seq4:     '#7E2B52',
  // Reserved for state only — never for decoration, and never on a figure that
  // is merely large. The previous values failed contrast on the light theme.
  good:     '#3DD68C',
  warn:     '#F0A23C',
  bad:      '#F2555A',
  hover:    'rgba(244,244,241,0.055)',
  inputBg:  '#181A1D',
};

export const LIGHT = {
  bg:       '#F6F7F9',
  s1:       '#FFFFFF',
  s2:       '#F0F1F4',
  s3:       '#E8EAEE',
  s4:       '#DDE0E6',
  text1:    '#090909',
  text2:    '#36393D',
  text3:    '#5F646A',
  text4:    '#747A81',
  border:   'rgba(24,25,27,0.11)',
  borderMd: 'rgba(24,25,27,0.18)',
  borderHi: 'rgba(24,25,27,0.30)',
  accent:   '#FF63AA',
  accentOn: '#090909',
  brand:    '#B53672',
  brandOn:  '#FFFFFF',
  // Same ramp inverted for a white surface: darkest carries the largest share.
  seq1:     '#8E2355',
  seq2:     '#D6418A',
  seq3:     '#F58CBD',
  seq4:     '#FBC7DE',
  good:     '#15803D',
  warn:     '#B45309',
  bad:      '#C02626',
  hover:    'rgba(24,25,27,0.05)',
  inputBg:  '#F0F1F2',
};
